import { createContext, useContext } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { ChevronRight, ChevronDown, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MrsLink } from '@/components/MrsLink';
import type { PicklistCreationTableRow, MrsTableRow, ItemTableRow, ItemSelectionState } from '../types';
import { useMaterialRequestStore, usePendingQuantityStore } from '../store';
import { StockStatusBadge } from './StockStatusBadge';
import { stockStatusOf, useStockStore, type StockStatus } from '@features/inventory';
import { freeStockStatus } from '../utils/freeStock';

const columnHelper = createColumnHelper<PicklistCreationTableRow>();

// Helper to check if row is MRS (parent) row
const isMrsRow = (row: PicklistCreationTableRow): row is MrsTableRow => row.rowType === 'mrs';

// Helper to check if row is Item (child) row
const isItemRow = (row: PicklistCreationTableRow): row is ItemTableRow => row.rowType === 'item';

// Get store action directly without creating subscription per row
const handleViewDetails = (mrsId: number) => {
  const store = useMaterialRequestStore.getState();
  const request = store.requests.find(r => r.id === mrsId);
  if (request) {
    store.openDetailSheet(request);
  }
};

// Helper to get selection state for a parent row
const isSelectableItem = (item: ItemTableRow): boolean =>
  !item.isNonCatalog && item.netsuiteItemId !== 0;

const getParentSelectionState = (
  row: MrsTableRow,
  itemSelection: ItemSelectionState
): 'none' | 'some' | 'all' => {
  const mrsSelection = itemSelection[String(row.mrsId)];
  if (!mrsSelection) return 'none';

  // Only selectable (stockable, linked) items count toward the parent state — non-catalog
  // and unlinked rows can never be selected, so requiring them would wedge "all" (86d3gmemk).
  const selectableCount = row.subRows.filter(isSelectableItem).length;
  if (selectableCount === 0) return 'none';

  const selectedCount = Object.values(mrsSelection).filter(Boolean).length;
  if (selectedCount === 0) return 'none';
  if (selectedCount >= selectableCount) return 'all';
  return 'some';
};


/**
 * Selection reaches the checkbox through CONTEXT, not through the column definitions.
 *
 * ⚡ This is a performance boundary, not a style choice. When `createExpandableColumns` closed over
 * `itemSelection`, every tick produced new column definitions, so TanStack re-rendered EVERY cell of
 * EVERY row — 39 rows and 148 tooltip roots on a default page. Measured: 239ms for the first tick,
 * 321ms for the second, against a 100ms budget.
 *
 * With the definitions stable, a tick re-renders only the checkboxes that read this context.
 */
interface SelectionContextValue {
  itemSelection: ItemSelectionState;
  onItemSelectionChange: (mrsId: number, itemId: number | 'all', selected: boolean) => void;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

export const PicklistSelectionProvider = SelectionContext.Provider;

function useSelection(): SelectionContextValue {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('picklist columns: PicklistSelectionProvider is missing');
  return ctx;
}

/** The select checkbox for one row — the only thing a tick needs to re-render. */
function SelectCell({ data }: { data: PicklistCreationTableRow }) {
  const { itemSelection, onItemSelectionChange } = useSelection();

  if (isMrsRow(data)) {
    const selectionState = getParentSelectionState(data, itemSelection);
    return (
      <Checkbox
        checked={selectionState === 'all' || (selectionState === 'some' && 'indeterminate')}
        onCheckedChange={(checked) => {
          onItemSelectionChange(data.mrsId, 'all', !!checked);
        }}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select all items from ${data.mrsNumber}`}
      />
    );
  }

  if (isItemRow(data)) {
    const isSelected = itemSelection[String(data.mrsId)]?.[String(data.itemId)] ?? false;

    // Non-stockable (Service/dummy) and unlinked (id 0) lines can't be picked — disable
    // selection and explain why, instead of letting them into the cart (86d3gmemk).
    const nonSelectable = data.isNonCatalog || data.netsuiteItemId === 0;
    if (nonSelectable) {
      const reason = data.isNonCatalog
        ? 'Non-catalog / manual item — cannot be added to a picklist'
        : 'Not linked to a NetSuite item — resync this MRS first';
      return (
        <div className="pl-6">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex" aria-label={reason}>
                  <Checkbox checked={false} disabled aria-label={reason} onClick={(e) => e.stopPropagation()} />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{reason}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    return (
      <div className="pl-6">
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => {
            onItemSelectionChange(data.mrsId, data.itemId, !!checked);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${data.itemCode}`}
        />
      </div>
    );
  }

  return null;
}

/**
 * The stock badge for one item, subscribed to THAT item`s slice of the stores.
 *
 * ⚡ Same boundary as SelectCell: the column definitions used to carry the stock getters and a cache
 * object in their dependencies, so one arriving stock read rebuilt every column. Subscribing per
 * item means an arriving read repaints one badge.
 *
 * 86d3d1z40 — everything below is DERIVED from the subscribed values. It used to subscribe, discard
 * the values (`void entry`) and call the store getters in render; the React Compiler memoised those
 * calls on the stable getter references, so the badge stayed "Not checked" after the stock arrived
 * while the cart, which reads the data, showed it.
 */
function StockCell({ data }: { data: ItemTableRow }) {
  const id = data.netsuiteItemId;
  const entry = useStockStore((s) => s.summaryCache[String(id)]);
  const isLoading = useStockStore((s) => s.loadingItems.has(id) || s._pendingItemIds.has(id));
  const errored = useStockStore((s) => !!s.errorItems[String(id)]);
  const pending = usePendingQuantityStore((s) => s.cache[String(id)]?.data ?? null);
  const pendingLoading = usePendingQuantityStore((s) => s.loadingItems.has(id) || s._pendingItemIds.has(id));
  const stock = entry?.data ?? null;

  // Non-stockable / unlinked lines get an explicit labeled state instead of a stock lookup they can
  // never resolve (86d3gmemk). Stockable lines are judged on FREE stock — on-hand less what other open
  // picklists hold — the figure the cart plans from (14ykpqtv7nd).
  const status: StockStatus = data.isNonCatalog
    ? 'non_catalog'
    : id === 0
      ? 'not_linked'
      : freeStockStatus(
          stockStatusOf(entry, data.availableQuantity, isLoading, errored),
          stock?.quantityAvailable,
          pending?.totalCommittedQuantity,
          pendingLoading,
          data.availableQuantity,
        );

  return (
    <StockStatusBadge
      status={status}
      quantityAvailable={stock?.quantityAvailable}
      requestedQuantity={data.availableQuantity}
      binCodes={stock ? Array.from(new Set(stock.bins.map((b) => b.binCode))) : undefined}
      pendingPickingQty={pending?.pendingPickingQuantity}
      pendingPostingQty={pending?.pendingPostingQuantity}
    />
  );
}

export const createExpandableColumns = () => [
  // Checkbox column
  columnHelper.display({
    id: 'select',
    header: () => null, // No header checkbox for hierarchical selection
    cell: ({ row }) => <SelectCell data={row.original} />,
    size: 50,
  }),

  // Expand/Collapse column
  columnHelper.display({
    id: 'expand',
    header: () => null,
    cell: ({ row }) => {
      if (!row.getCanExpand()) return null;

      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
        >
          {row.getIsExpanded() ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      );
    },
    size: 40,
  }),

  // MRS Number / Item Code column
  columnHelper.display({
    id: 'identifier',
    header: 'MRS / Item',
    cell: ({ row }) => {
      const data = row.original;

      if (isMrsRow(data)) {
        return (
          <MrsLink mrsId={data.mrsId} mrsNumber={data.mrsNumber} className="font-medium" />
        );
      }

      if (isItemRow(data)) {
        const clsLine = [data.netsuiteItemClass, data.netsuiteItemSubClass]
          .filter(Boolean)
          .join(' › ');
        // 86d48d39v: ItemName is uncapped in the DB (750 chars in prod today). The clamp bounds the
        // row height; the tooltip is what keeps the full spec readable, so it carries everything.
        const fullTip = `${data.itemCode} · ${data.itemName}${clsLine ? ` — ${clsLine}` : ''}`;

        // Item identity is ONE cell spanning identifier + description — ItemSelectionPanel gives this
        // cell colSpan={2} and skips the description cell for item rows.
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex min-w-0 cursor-help flex-col pl-3.5" aria-label={fullTip}>
                <span className="line-clamp-2 text-[12.5px] leading-[1.35]">
                  <span className="font-mono font-semibold">{data.itemCode}</span> · {data.itemName}
                </span>
                {clsLine && (
                  <span className="truncate text-[11px] leading-[1.35] text-muted-foreground">
                    {clsLine}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="text-xs">{fullTip}</p>
            </TooltipContent>
          </Tooltip>
        );
      }

      return null;
    },
  }),

  // Requestor column — MRS rows only. Item rows fold their name into `identifier` (see above), and
  // ItemSelectionPanel skips this cell for them entirely.
  columnHelper.display({
    id: 'description',
    header: 'Requestor',
    cell: ({ row }) => {
      const data = row.original;

      if (isMrsRow(data)) {
        return (
          <span className="block min-w-0 text-[13px] font-medium leading-[1.35]">
            {data.requestorFullName}
          </span>
        );
      }

      return null;
    },
  }),

  // Department / End Use column (only for item rows)
  columnHelper.display({
    id: 'deptEndUse',
    // Blank: the per-group item sub-header labels this column (the header row above labels
    // MRS-level columns only, and Dept / End Use is an item-level attribute).
    header: '',
    cell: ({ row }) => {
      const data = row.original;

      if (isItemRow(data)) {
        const dept = data.netsuiteDepartmentName;
        const endUse = data.netsuiteEndUseName;

        if (!dept && !endUse) return '-';

        return (
          // 12.5px, not 14: "ROLLING MILL 1" over "WATER TREATMENT PLANT" is two long names in a
          // narrow slot, and the extra width alone still left them stacking four lines deep.
          <div className="flex flex-col text-[12.5px] leading-[1.3]">
            {dept && <span>{dept}</span>}
            {endUse && <span className="text-muted-foreground">{endUse}</span>}
          </div>
        );
      }

      return null;
    },
  }),

  // Date / Quantity column
  columnHelper.display({
    id: 'dateOrQty',
    header: 'Date Needed',
    cell: ({ row }) => {
      const data = row.original;

      if (isMrsRow(data)) {
        // nowrap so the date never splits after the comma — "Sep 15," over "2026" reads as two
        // facts. The column budget gives it the room; this makes sure it uses it.
        return (
          <span className="whitespace-nowrap">
            {new Date(data.requestedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        );
      }

      if (isItemRow(data)) {
        // `availableQuantity` is already NET of what the target picklist holds, so on the Add Items
        // route it can read 2 where the MRS asked for 4 with nothing to explain the gap. Naming what the
        // picklist already carries is the whole answer, and it is why the line is no longer hidden
        // (owner report 2026-09-21).
        return (
          <div className="flex flex-col">
            <span className="whitespace-nowrap">
              {data.availableQuantity} {data.unitOfMeasure}
            </span>
            {data.alreadyOnPicklist > 0 && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {data.alreadyOnPicklist} {data.unitOfMeasure} already on this picklist
              </span>
            )}
          </div>
        );
      }

      return null;
    },
  }),

  // Stock Status column (only for item rows)
  columnHelper.display({
    id: 'stockStatus',
    // Blank: labelled by the item sub-header — stock only ever renders on item rows.
    header: '',
    cell: ({ row }) =>
      isItemRow(row.original) ? <StockCell data={row.original} /> : null,
    size: 120,
  }),

  // Approved By column (only for MRS rows)
  columnHelper.display({
    id: 'approvedBy',
    header: 'Approved By',
    cell: ({ row }) => {
      const data = row.original;

      if (isMrsRow(data)) {
        return data.approvedBy || '-';
      }

      return null;
    },
  }),

  // Items count column (only for MRS rows)
  columnHelper.display({
    id: 'itemCount',
    header: 'Items',
    cell: ({ row }) => {
      const data = row.original;

      if (isMrsRow(data)) {
        // `whitespace-nowrap` is what widens this column: the table is auto-layout, so a cell that
        // refuses to wrap forces the column to the width its content actually needs. Without it
        // "6 item(s)" breaks across two lines and the pill grows a second row.
        return (
          <Badge variant="secondary" className="whitespace-nowrap">
            {data.itemCount} item(s)
          </Badge>
        );
      }

      return null;
    },
  }),

  // Actions column (only for MRS rows)
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const data = row.original;

      if (isMrsRow(data)) {
        return (
          // A TRUE 28px icon button. `size="sm"` alone is 40px wide (its own `px-3`), which is wider than this
          // column: in a fixed table that spills past the table's right edge and makes the whole table scroll
          // sideways at every width (owner, 2026-10-05). Native `title`, not a Radix tooltip: one per MRS row.
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(data.mrsId);
            }}
            title={`View details of ${data.mrsNumber}`}
            aria-label={`View details of ${data.mrsNumber}`}
          >
            <Eye className="h-4 w-4" aria-hidden />
          </Button>
        );
      }

      return null;
    },
    size: 44,
  }),
];
